import React, { useState } from 'react';
import {
  Stethoscope,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Wrench,
  Shield,
  Copy,
  Check,
  X,
  Server,
  Layers,
  Globe,
  Database,
} from 'lucide-react';
import { api } from '../lib/api';

export interface DiagnosticCheck {
  id: string;
  name: string;
  status: 'PASS' | 'WARN' | 'FAIL';
  category: 'DATABASE' | 'KONG_GATEWAY' | 'ACL_PERMISSIONS' | 'SECURITY';
  message: string;
  details?: Record<string, any>;
}

export interface DiagnosticResult {
  timestamp: string;
  application_id: string;
  application_name: string;
  overall_status: 'HEALTHY' | 'WARNING' | 'DRIFT_DETECTED';
  self_heal_recommended: boolean;
  summary: {
    total_checks: number;
    passed: number;
    warnings: number;
    failures: number;
  };
  checks: DiagnosticCheck[];
}

interface ApiKeyDiagnosticModalProps {
  applicationId: string;
  clientId: string;
  applicationName: string;
  triggerButtonVariant?: 'icon' | 'compact' | 'full';
  customButtonText?: string;
  onDiagnosticComplete?: (result: DiagnosticResult) => void;
}

export const ApiKeyDiagnosticModal: React.FC<ApiKeyDiagnosticModalProps> = ({
  applicationId,
  clientId,
  applicationName,
  triggerButtonVariant = 'compact',
  customButtonText,
  onDiagnosticComplete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isHealing, setIsHealing] = useState(false);
  const [diagnosticResult, setDiagnosticResult] = useState<DiagnosticResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copiedReport, setCopiedReport] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>('ALL');

  const runDiagnostic = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const response = await api.post<{ success: boolean; data: DiagnosticResult }>(
        `/clients/${clientId}/applications/${applicationId}/key-diagnostic`
      );
      if (response.data?.success && response.data?.data) {
        setDiagnosticResult(response.data.data);
        onDiagnosticComplete?.(response.data.data);
      } else {
        setErrorMessage('Não foi possível obter os dados do diagnóstico.');
      }
    } catch (err: any) {
      setErrorMessage(
        err.response?.data?.error?.message ||
          'Erro ao comunicar com o serviço de diagnóstico do Gateway.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const runSelfHeal = async () => {
    setIsHealing(true);
    setErrorMessage(null);
    try {
      await api.post(`/clients/${clientId}/applications/${applicationId}/key-self-heal`);
      // Re-run diagnostic immediately to verify resolution
      await runDiagnostic();
    } catch (err: any) {
      setErrorMessage(
        err.response?.data?.error?.message || 'Falha ao executar auto-recuperação da chave no Gateway.'
      );
    } finally {
      setIsHealing(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    runDiagnostic();
  };

  const handleCopyReport = () => {
    if (!diagnosticResult) return;
    navigator.clipboard.writeText(JSON.stringify(diagnosticResult, null, 2));
    setCopiedReport(true);
    setTimeout(() => setCopiedReport(false), 2000);
  };

  const filteredChecks = diagnosticResult
    ? filterCategory === 'ALL'
      ? diagnosticResult.checks
      : diagnosticResult.checks.filter((c) => c.category === filterCategory)
    : [];

  const getCategoryIcon = (category: DiagnosticCheck['category']) => {
    switch (category) {
      case 'DATABASE':
        return <Database className="w-3.5 h-3.5 text-blue-500" />;
      case 'KONG_GATEWAY':
        return <Server className="w-3.5 h-3.5 text-purple-500" />;
      case 'ACL_PERMISSIONS':
        return <Layers className="w-3.5 h-3.5 text-emerald-500" />;
      case 'SECURITY':
        return <Shield className="w-3.5 h-3.5 text-amber-500" />;
      default:
        return <Globe className="w-3.5 h-3.5" />;
    }
  };

  return (
    <>
      {/* Trigger Button */}
      {triggerButtonVariant === 'icon' && (
        <button
          type="button"
          onClick={handleOpen}
          className="p-1.5 rounded-lg text-slate-500 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-white dark:hover:bg-slate-700 transition-colors"
          title="Diagnosticar Chave & Sincronização Kong"
          aria-label="Diagnosticar Chave"
        >
          <Stethoscope className="w-3.5 h-3.5" />
        </button>
      )}

      {triggerButtonVariant === 'compact' && (
        <button
          type="button"
          onClick={handleOpen}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 transition-colors"
        >
          <Stethoscope className="w-3.5 h-3.5 text-primary-500" />
          <span>{customButtonText || 'Diagnosticar Chave'}</span>
        </button>
      )}

      {triggerButtonVariant === 'full' && (
        <button
          type="button"
          onClick={handleOpen}
          className="btn-base btn-secondary flex items-center justify-center gap-2 text-xs py-2 px-3.5"
        >
          <Stethoscope className="w-4 h-4 text-primary-500" />
          <span>{customButtonText || 'Diagnosticar Chave & Sincronização Kong'}</span>
        </button>
      )}

      {/* Modal Backdrop & Window */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto animate-fade-in"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden my-8">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-primary-100 dark:bg-primary-950/60 text-primary-600 dark:text-primary-400">
                  <Stethoscope className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <span>Diagnóstico de Chave & Gateway Kong</span>
                    <span className="text-2xs px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 font-mono">
                      {applicationName}
                    </span>
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Inspeção de integridade criptográfica, provisão no Kong e autorizações ACL
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
                aria-label="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 sm:p-6 space-y-5 max-h-[72vh] overflow-y-auto">
              {/* Error Notice */}
              {errorMessage && (
                <div className="p-3.5 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 flex items-start gap-3">
                  <XCircle className="w-4 h-4 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-rose-700 dark:text-rose-300">{errorMessage}</p>
                </div>
              )}

              {/* Loading State */}
              {isLoading && (
                <div className="py-12 flex flex-col items-center justify-center text-center space-y-3">
                  <RefreshCw className="w-8 h-8 text-primary-500 animate-spin" />
                  <div>
                    <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200">
                      Examinando Chave e Gateway...
                    </h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      Consultando PostgreSQL, clusters Kong, consumidores e tabelas ACL
                    </p>
                  </div>
                </div>
              )}

              {/* Diagnostic Results Presentation */}
              {!isLoading && diagnosticResult && (
                <>
                  {/* Overall Pulse Banner */}
                  <div
                    className={`p-4 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-3.5 transition-all ${
                      diagnosticResult.overall_status === 'HEALTHY'
                        ? 'bg-emerald-50/70 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900/60'
                        : diagnosticResult.overall_status === 'WARNING'
                          ? 'bg-amber-50/70 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900/60'
                          : 'bg-rose-50/70 dark:bg-rose-950/30 border-rose-200 dark:border-rose-900/60'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`p-2 rounded-xl shrink-0 ${
                          diagnosticResult.overall_status === 'HEALTHY'
                            ? 'bg-emerald-100 dark:bg-emerald-900/60 text-emerald-600 dark:text-emerald-400'
                            : diagnosticResult.overall_status === 'WARNING'
                              ? 'bg-amber-100 dark:bg-amber-900/60 text-amber-600 dark:text-amber-400'
                              : 'bg-rose-100 dark:bg-rose-900/60 text-rose-600 dark:text-rose-400'
                        }`}
                      >
                        {diagnosticResult.overall_status === 'HEALTHY' ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : diagnosticResult.overall_status === 'WARNING' ? (
                          <AlertTriangle className="w-5 h-5" />
                        ) : (
                          <XCircle className="w-5 h-5" />
                        )}
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                          <span>
                            {diagnosticResult.overall_status === 'HEALTHY'
                              ? 'Chave & Gateway Saudáveis'
                              : diagnosticResult.overall_status === 'WARNING'
                                ? 'Avisos de Segurança / Restrições Ativas'
                                : 'Deriva Detectada no Gateway'}
                          </span>
                          <span
                            className={`text-2xs font-mono font-bold px-2 py-0.5 rounded-full ${
                              diagnosticResult.overall_status === 'HEALTHY'
                                ? 'bg-emerald-200 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-300'
                                : diagnosticResult.overall_status === 'WARNING'
                                  ? 'bg-amber-200 dark:bg-amber-900 text-amber-800 dark:text-amber-300'
                                  : 'bg-rose-200 dark:bg-rose-900 text-rose-800 dark:text-rose-300'
                            }`}
                          >
                            {diagnosticResult.summary.passed}/{diagnosticResult.summary.total_checks}{' '}
                            Verificações OK
                          </span>
                        </h4>
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                          {diagnosticResult.overall_status === 'HEALTHY'
                            ? 'A chave está cifrada com segurança, o consumidor existe no Kong e todas as permissões ACL estão alinhadas.'
                            : diagnosticResult.overall_status === 'WARNING'
                              ? 'A chave funciona, mas existem restrições de IP de origem ativas que podem filtrar requisições.'
                              : 'Existe divergência entre o banco de dados e o Kong. Use a auto-recuperação abaixo para reparar.'}
                        </p>
                      </div>
                    </div>

                    {/* Self-Heal Action in Banner if recommended */}
                    {diagnosticResult.self_heal_recommended && (
                      <button
                        type="button"
                        onClick={runSelfHeal}
                        disabled={isHealing}
                        className="px-3.5 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl shadow-xs transition-colors shrink-0 flex items-center gap-1.5 disabled:opacity-50"
                      >
                        <Wrench className={`w-3.5 h-3.5 ${isHealing ? 'animate-spin' : ''}`} />
                        <span>{isHealing ? 'Reparando...' : 'Reparar Agora'}</span>
                      </button>
                    )}
                  </div>

                  {/* Category Filter Tabs */}
                  <div className="flex items-center gap-1.5 border-b border-slate-200 dark:border-slate-800 pb-2 overflow-x-auto text-xs">
                    {[
                      { id: 'ALL', label: 'Todas as Verificações' },
                      { id: 'DATABASE', label: 'Banco de Dados' },
                      { id: 'KONG_GATEWAY', label: 'Kong Gateway' },
                      { id: 'ACL_PERMISSIONS', label: 'Permissões ACL' },
                      { id: 'SECURITY', label: 'Segurança & IPs' },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setFilterCategory(tab.id)}
                        className={`px-2.5 py-1 rounded-lg font-medium whitespace-nowrap transition-colors ${
                          filterCategory === tab.id
                            ? 'bg-primary-50 dark:bg-primary-950/60 text-primary-600 dark:text-primary-400 font-bold'
                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Checks List */}
                  <div className="space-y-2.5">
                    {filteredChecks.map((check) => (
                      <div
                        key={check.id}
                        className="p-3 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 flex items-start justify-between gap-3"
                      >
                        <div className="flex items-start gap-2.5">
                          <div className="mt-0.5 shrink-0">
                            {check.status === 'PASS' && (
                              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            )}
                            {check.status === 'WARN' && (
                              <AlertTriangle className="w-4 h-4 text-amber-500" />
                            )}
                            {check.status === 'FAIL' && (
                              <XCircle className="w-4 h-4 text-rose-500" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-slate-900 dark:text-white">
                                {check.name}
                              </span>
                              <span className="flex items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400 font-mono bg-white dark:bg-slate-800 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700">
                                {getCategoryIcon(check.category)}
                                <span>{check.category}</span>
                              </span>
                            </div>
                            <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                              {check.message}
                            </p>
                            {check.details && (
                              <div className="mt-1.5 text-[11px] font-mono text-slate-500 dark:text-slate-400 bg-white/80 dark:bg-slate-900/60 p-2 rounded-lg border border-slate-100 dark:border-slate-800">
                                {Object.entries(check.details).map(([k, v]) => (
                                  <div key={k} className="flex items-center gap-1.5 truncate">
                                    <span className="text-slate-400 dark:text-slate-500">{k}:</span>
                                    <span className="text-slate-800 dark:text-slate-200 font-semibold">
                                      {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        <span
                          className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0 ${
                            check.status === 'PASS'
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400'
                              : check.status === 'WARN'
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400'
                                : 'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-400'
                          }`}
                        >
                          {check.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={runDiagnostic}
                  disabled={isLoading}
                  className="px-3 py-1.5 text-xs font-semibold rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                  <span>Re-testar</span>
                </button>
                <button
                  type="button"
                  onClick={handleCopyReport}
                  disabled={!diagnosticResult}
                  className="px-3 py-1.5 text-xs font-semibold rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                  title="Copiar Relatório em JSON para Suporte"
                >
                  {copiedReport ? (
                    <Check className="w-3.5 h-3.5 text-emerald-500" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                  <span>{copiedReport ? 'Copiado!' : 'Copiar JSON'}</span>
                </button>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={runSelfHeal}
                  disabled={isHealing || isLoading}
                  className="px-3.5 py-1.5 text-xs font-bold rounded-xl bg-primary-600 hover:bg-primary-700 text-white shadow-xs transition-colors flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Wrench className={`w-3.5 h-3.5 ${isHealing ? 'animate-spin' : ''}`} />
                  <span>{isHealing ? 'Sincronizando...' : 'Auto-Recuperar Gateway'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="px-3 py-1.5 text-xs font-semibold rounded-xl text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
